import { useDispatch } from 'react-redux';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getDocumentBlobUrl, getInstructionDocumentBlobUrl } from '../utils';
import { enqueueSnackbar } from '@/src/actions/snackbar';
import { useTranslation } from '@/src/i18n';

type GetDocumentUrlProps = {
  documentId?: string;
  instructionId?: string;
  showSnackbar?: boolean;
};

const useDocumentUrl = ({
  documentId,
  instructionId,
  showSnackbar,
}: GetDocumentUrlProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const getDocumentUrl = React.useCallback(async () => {
    if (!documentId) {
      return null;
    }

    if (!!instructionId === true) {
      return getInstructionDocumentBlobUrl(
        dispatch,
        instructionId,
        documentId,
      ).then(url => url);
    }

    return getDocumentBlobUrl(dispatch, documentId).then(url => url);
  }, [documentId, instructionId]);

  const query = useQuery({
    enabled: !!documentId,
    queryKey: [
      'documentUrl',
      `documentId:${documentId}`,
      `instructionId:${instructionId}`,
    ],
    queryFn: getDocumentUrl,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
  });

  const { isFetching, isError } = query;

  // loading snackbar if enabled
  React.useEffect(() => {
    if (showSnackbar && isFetching) {
      dispatch(
        enqueueSnackbar({
          message: t('loading'),
          duration: 1000,
          severity: 'info',
        }),
      );
    }
  }, [showSnackbar, isFetching]);

  // error snackbar if enabled
  React.useEffect(() => {
    if (showSnackbar && isError) {
      dispatch(
        enqueueSnackbar({
          message: t('error:generic_message'),
          duration: 5000,
          severity: 'error',
        }),
      );
    }
  }, [showSnackbar, isError]);

  return query;
};

export default useDocumentUrl;
